import * as React from "react";

import { useGetFolderDocumentMapQuery } from "@/api/document-map";
import type { ContractSummaryStatus } from "@/types/supplier";

const POLL_INTERVAL_MS = 15_000;
const RUNNING_SUMMARY_STATUSES: ReadonlySet<ContractSummaryStatus | undefined> = new Set([
  "in_progress",
  "needs_review",
]);

/** The folder's map, polled while it generates and read again when its contract summary run ends. */
export const useFamilyFolderDocumentMap = (
  folderFseId: string | undefined,
  enabled: boolean,
  summaryStatus: ContractSummaryStatus | undefined,
) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { data, isError, refetch } = useGetFolderDocumentMapQuery(folderFseId ?? "", {
    skip: !enabled,
    pollingInterval: isGenerating ? POLL_INTERVAL_MS : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });
  const documentMap = enabled ? data : undefined;

  React.useEffect(() => {
    setIsGenerating(documentMap?.status === "generating");
  }, [documentMap?.status]);

  const wasSummaryRunning = React.useRef(false);
  React.useEffect(() => {
    const isRunning = RUNNING_SUMMARY_STATUSES.has(summaryStatus);
    // Publication marks the map as generating, so no poll is needed until the run has ended.
    if (enabled && wasSummaryRunning.current && !isRunning) void refetch();
    wasSummaryRunning.current = isRunning;
  }, [enabled, summaryStatus, refetch]);

  return { documentMap, isError: enabled && isError };
};
